import React, { useState } from 'react';
import { useProjectContext } from "../contexts/ProjectContext";
import styles from './Debug.module.scss';

const Debug: React.FC = () => {
  const { selectedProjectId } = useProjectContext();
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');

  const handleSync = async (endpoint: string, params: Record<string, string> = {}) => {
    setIsProcessing(true);
    setMessage('実行中...');

    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `${process.env.REACT_APP_BACKEND_URL}/${endpoint}${queryParams ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMessage(`成功: ${data.message || JSON.stringify(data)}`);
      } else {
        setMessage('エラーが発生しました。');
      }
    } catch (error) {
      console.error("Sync error:", error);
      setMessage('通信エラーが発生しました。');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderSyncCard = (
    title: string,
    endpoint: string,
    params: Record<string, string>
  ) => (
    <section className={styles.syncCard}>
      <h4 className={styles.cardTitle}>{title}</h4>
      <div className={styles.buttonGroup}>
        <button
          onClick={() => handleSync(endpoint, { ...params, force: 'false' })}
          disabled={isProcessing || !selectedProjectId}
          className={`${styles.button} ${styles.normalButton}`}
        >
          通常同期 (直近3日)
        </button>
        <button
          onClick={() => {
            if (window.confirm('180日分全データを再ロードしますか？')) {
              handleSync(endpoint, { ...params, force: 'true' });
            }
          }}
          disabled={isProcessing || !selectedProjectId}
          className={`${styles.button} ${styles.dangerButton}`}
        >
          フルロード (180日)
        </button>
      </div>
    </section>
  );

  const renderActionCard = (
    title: string,
    endpoint: string,
    params: Record<string, string>
  ) => (
    <section className={styles.syncCard}>
      <h4 className={styles.cardTitle}>{title}</h4>
      <div className={styles.buttonGroup}>
        <button
          onClick={() => handleSync(endpoint, params)}
          disabled={isProcessing || !selectedProjectId}
          className={`${styles.button} ${styles.normalButton}`}
        >
          Settingロード
        </button>
      </div>
    </section>
  );

  const projectParams = { project_id: String(selectedProjectId || '') };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Debug PROJECT：{selectedProjectId}</h1>
      <p className={styles.description}>※実行状況はサーバーログを確認</p>

      <div className={styles.container}>

        <section className={styles.rowSection}>
          <h3 className={styles.sectionTitle}>1. 広告媒体 → BigQuery 同期 (選択PJ)</h3>
          <div className={styles.rowCards}>
            {renderSyncCard('GA4', 'ga4-to-bq-project', projectParams)}
            {renderSyncCard('Google Ads', 'google-ads-to-bq-project', projectParams)}
            {renderSyncCard('Meta Ads', 'meta-ads-to-bq-project', projectParams)}
            {renderSyncCard('Yahoo Search Ads', 'yahoo-search-ads-to-bq-project', projectParams)}
            {renderSyncCard('Yahoo Display Ads', 'yahoo-display-ads-to-bq-project', projectParams)}
          </div>
        </section>

        <section className={styles.rowSection}>
          <h3 className={styles.sectionTitle}>2. BigQuery → MySQL 同期 (選択PJ)</h3>
          <div className={styles.rowCards}>
            {renderSyncCard('GA4', 'ga4-bq-to-mysql-project', projectParams)}
            {renderSyncCard('Google Ads', 'google-ads-bq-to-mysql-project', projectParams)}
            {renderSyncCard('Meta Ads', 'meta-ads-bq-to-mysql-project', projectParams)}
            {renderSyncCard(
              'Yahoo Search Ads',
              'yahoo-search-ads-bq-to-mysql-project',
              projectParams
            )}
            {renderSyncCard(
              'Yahoo Display Ads',
              'yahoo-display-ads-bq-to-mysql-project',
              projectParams
            )}
          </div>
        </section>

        <section className={styles.rowSection}>
          <h3 className={styles.sectionTitle}>3. Settings 同期 (選択PJ)</h3>
          <div className={styles.rowCards}>
            {renderActionCard('Google Ads Settings', 'google-ads-settings-project', projectParams)}
            {renderActionCard('Meta Ads Settings', 'meta-ads-settings-project', projectParams)}
            {renderActionCard(
              'Yahoo Search Ads Settings',
              'yahoo-search-ads-settings-project',
              projectParams
            )}
            {renderActionCard(
              'Yahoo Display Ads Settings',
              'yahoo-display-ads-settings-project',
              projectParams
            )}
          </div>
        </section>

        {!selectedProjectId && (
          <p className={styles.disabledText}>
            プロジェクトを選択すると実行できます
          </p>
        )}

        {message && (
          <div className={`${styles.message} ${message.includes('成功') ? styles.successMessage : ''}`}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default Debug;
